

import { useState, useEffect } from "react";
import { useToast } from "@/context/ToastContext";
import { eventsApi, vendorsApi, mastersApi } from "@/lib/api";
import SearchableDropdown from "../../components/SearchableDropdown";

interface VendorOption {
  id: number;
  name: string;
  vendor_type?: { id: number; name: string };
  commission_type?: string;
  commission_value?: number;
}

interface AddVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  onVendorAdded: () => void;
}

const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">{children}</p>
);

export default function AddVendorModal({
  isOpen,
  onClose,
  eventId,
  onVendorAdded,
}: AddVendorModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [commissionAutoFilled, setCommissionAutoFilled] = useState(false);

  // Quick-create vendor state
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [vendorTypes, setVendorTypes] = useState<{ id: number; name: string }[]>([]);
  const [quickForm, setQuickForm] = useState({
    name: "",
    vendor_type_id: "",
    phone: "",
    contact_person: "",
  });
  const [quickErrors, setQuickErrors] = useState<Partial<typeof quickForm>>({});

  const [formData, setFormData] = useState({
    vendor_id: undefined as number | undefined,
    description: "",
    quantity: 1,
    unit: "unit",
    unit_price: 0,
    cost_price: 0,
    commission_value: 0,
    commission_type: "percentage",
    notes: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});

  useEffect(() => {
    if (isOpen) {
      loadVendors();
      loadVendorTypes();
      resetForm();
    }
  }, [isOpen]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorsApi.list("for_select=true");
      let vendorData: VendorOption[] = [];
      if (Array.isArray(response)) vendorData = response;
      else if (response?.data?.data && Array.isArray(response.data.data)) vendorData = response.data.data;
      else if (response?.data && Array.isArray(response.data)) vendorData = response.data;
      else if (response?.vendors && Array.isArray(response.vendors)) vendorData = response.vendors;
      setVendors(vendorData);
    } catch {
      showToast("Failed to load vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadVendorTypes = async () => {
    try {
      const response = await mastersApi.vendorTypes.list();
      const types = response.data?.data || response.data || [];
      setVendorTypes(types);
    } catch {
      // non-critical
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: undefined,
      description: "",
      quantity: 1,
      unit: "unit",
      unit_price: 0,
      cost_price: 0,
      commission_value: 0,
      commission_type: "percentage",
      notes: "",
    });
    setErrors({});
    setCommissionAutoFilled(false);
    setShowQuickCreate(false);
    setQuickForm({ name: "", vendor_type_id: "", phone: "", contact_person: "" });
    setQuickErrors({});
  };

  const handleChange = (field: keyof typeof formData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleVendorSelect = (vendorId: string) => {
    const vendor = vendors.find((v) => String(v.id) === vendorId);
    if (vendor) {
      const hasStoredCommission = (vendor.commission_value ?? 0) > 0;
      setFormData((prev) => ({
        ...prev,
        vendor_id: vendor.id,
        commission_value: vendor.commission_value ?? 0,
        commission_type: vendor.commission_type || "percentage",
      }));
      setCommissionAutoFilled(hasStoredCommission);
    }
  };

  const validateQuickForm = () => {
    const errs: Partial<typeof quickForm> = {};
    if (!quickForm.name.trim()) errs.name = "Required";
    if (!quickForm.vendor_type_id) errs.vendor_type_id = "Required";
    if (!quickForm.phone.trim()) errs.phone = "Required";
    setQuickErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleQuickCreate = async () => {
    if (!validateQuickForm()) return;
    setCreatingVendor(true);
    try {
      const response = await vendorsApi.create({
        name: quickForm.name.trim(),
        vendor_type_id: parseInt(quickForm.vendor_type_id),
        phone: quickForm.phone.trim(),
        contact_person: quickForm.contact_person.trim() || quickForm.name.trim(),
        commission_type: "percentage",
        commission_value: 0,
        status: "active",
      });
      const newVendor: VendorOption =
        response?.data?.data || response?.data || response;
      showToast(`Vendor "${newVendor.name}" created`, "success");
      await loadVendors();
      if (newVendor?.id) {
        setFormData((prev) => ({
          ...prev,
          vendor_id: newVendor.id,
          commission_value: 0,
          commission_type: "percentage",
        }));
        setCommissionAutoFilled(false);
      }
      setShowQuickCreate(false);
      setQuickForm({ name: "", vendor_type_id: "", phone: "", contact_person: "" });
      setQuickErrors({});
    } catch (err: unknown) {
      const message = err && typeof err === "object" && "message" in err
        ? String((err as { message: string }).message)
        : "Failed to create vendor";
      showToast(message, "error");
    } finally {
      setCreatingVendor(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};
    if (!formData.vendor_id) newErrors.vendor_id = "Vendor is required";
    if (!formData.description?.trim()) newErrors.description = "Service description is required";
    if (formData.quantity <= 0) newErrors.quantity = "Quantity must be > 0";
    if (formData.unit_price < 0) newErrors.unit_price = "Price cannot be negative";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateCost = () => {
    const unitPrice = formData.unit_price || 0;
    const commissionValue = formData.commission_value ?? 0;
    if (formData.commission_type === "percentage") {
      return unitPrice - (unitPrice * commissionValue) / 100;
    }
    return unitPrice - commissionValue;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const vendorCost = calculateCost();
    setSubmitting(true);
    try {
      await eventsApi.addEventVendor(eventId, {
        vendor_id: formData.vendor_id,
        description: formData.description,
        quantity: formData.quantity,
        unit: formData.unit,
        unit_price: formData.unit_price,
        cost_price: vendorCost,
        total_amount: vendorCost * formData.quantity,
        commission_value: formData.commission_value,
        commission_type: formData.commission_type,
        notes: formData.notes,
      });
      showToast("Vendor added successfully", "success");
      onClose();
      onVendorAdded();
    } catch (err: unknown) {
      const message = err && typeof err === "object" && "message" in err
        ? String((err as { message: string }).message)
        : "Failed to add vendor";
      showToast(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const lineTotal = formData.quantity * formData.unit_price;
  const vendorCost = calculateCost();
  const totalVendorCost = vendorCost * formData.quantity;
  const margin = lineTotal - totalVendorCost;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/60 backdrop-blur-sm flex items-end md:items-center justify-center">
      <div className="w-full bg-white flex flex-col md:max-w-xl md:rounded-2xl md:shadow-2xl overflow-hidden md:max-h-[92vh] max-h-[95vh] rounded-t-2xl">

        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100 bg-gradient-to-r from-blue-600 to-blue-500 flex-shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white leading-tight">Assign Vendor</h3>
              <p className="text-[10px] text-blue-100">Pricing & commission details</p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); onClose(); }}
            className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Vendor selection */}
          <div>
            <SectionLabel>Vendor</SectionLabel>
            {loading ? (
              <div className="w-full px-3 py-2.5 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-400 animate-pulse">
                Loading vendors...
              </div>
            ) : (
              <SearchableDropdown
                options={vendors.map((v) => ({ value: String(v.id), label: v.name }))}
                value={formData.vendor_id ? String(formData.vendor_id) : ""}
                onChange={handleVendorSelect}
                placeholder="Search and select vendor"
                error={errors.vendor_id}
              />
            )}

            {/* Quick-create toggle */}
            {!showQuickCreate ? (
              <button
                type="button"
                onClick={() => setShowQuickCreate(true)}
                className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                Add new vendor
              </button>
            ) : (
              /* Inline Quick-Create Form */
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2.5">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-bold text-blue-700">Quick Create Vendor</p>
                  <button
                    type="button"
                    onClick={() => { setShowQuickCreate(false); setQuickErrors({}); }}
                    className="text-blue-400 hover:text-blue-600 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={quickForm.name}
                      onChange={(e) => setQuickForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Vendor / business name *"
                      className={`w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 ${quickErrors.name ? "border-rose-300 bg-rose-50" : "border-blue-200 bg-white focus:border-blue-400"}`}
                    />
                    {quickErrors.name && <p className="text-[10px] text-rose-500 mt-0.5">{quickErrors.name}</p>}
                  </div>

                  <div>
                    <select
                      value={quickForm.vendor_type_id}
                      onChange={(e) => setQuickForm((p) => ({ ...p, vendor_type_id: e.target.value }))}
                      className={`w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 bg-white ${quickErrors.vendor_type_id ? "border-rose-300 bg-rose-50" : "border-blue-200 focus:border-blue-400"}`}
                    >
                      <option value="">Type *</option>
                      {vendorTypes.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {quickErrors.vendor_type_id && <p className="text-[10px] text-rose-500 mt-0.5">{quickErrors.vendor_type_id}</p>}
                  </div>

                  <div>
                    <input
                      type="tel"
                      value={quickForm.phone}
                      onChange={(e) => setQuickForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Phone *"
                      className={`w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-100 ${quickErrors.phone ? "border-rose-300 bg-rose-50" : "border-blue-200 bg-white focus:border-blue-400"}`}
                    />
                    {quickErrors.phone && <p className="text-[10px] text-rose-500 mt-0.5">{quickErrors.phone}</p>}
                  </div>

                  <div className="col-span-2">
                    <input
                      type="text"
                      value={quickForm.contact_person}
                      onChange={(e) => setQuickForm((p) => ({ ...p, contact_person: e.target.value }))}
                      placeholder="Contact person (optional)"
                      className="w-full px-3 py-2 rounded-lg border border-blue-200 bg-white text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleQuickCreate}
                  disabled={creatingVendor}
                  className="w-full py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {creatingVendor ? "Creating..." : "Create & Select Vendor"}
                </button>
              </div>
            )}
          </div>

          {/* Service */}
          <div>
            <SectionLabel>Service Details</SectionLabel>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="e.g., Photography, Catering, Decoration"
              className={`w-full px-3 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-blue-100 ${errors.description ? "border-rose-300 bg-rose-50 focus:border-rose-400" : "border-stone-200 focus:border-blue-400"}`}
            />
            {errors.description && <p className="text-xs text-rose-500 mt-0.5">{errors.description}</p>}

            <div className="grid grid-cols-3 gap-2 mt-2">
              <div>
                <label className="block text-[10px] font-semibold text-stone-500 mb-1">Qty</label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)}
                  className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm text-center focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-stone-500 mb-1">Unit</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-stone-500 mb-1">Unit Price</label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs font-bold">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => handleChange("unit_price", parseFloat(e.target.value) || 0)}
                    className="w-full pl-5 pr-1 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Commission */}
          <div>
            <SectionLabel>Commission</SectionLabel>
            {commissionAutoFilled && (
              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 mb-2">
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Auto-filled from vendor profile
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-stone-500 mb-1">Type</label>
                <select
                  value={formData.commission_type}
                  onChange={(e) => handleChange("commission_type", e.target.value)}
                  className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed (₹)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-stone-500 mb-1">
                  Value
                </label>
                <div className="relative">
                  {formData.commission_type === "fixed" && (
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 text-xs font-bold">₹</span>
                  )}
                  <input
                    type="number"
                    min="0"
                    step={formData.commission_type === "percentage" ? "1" : "0.01"}
                    max={formData.commission_type === "percentage" ? "100" : undefined}
                    value={formData.commission_value ?? 0}
                    onChange={(e) => handleChange("commission_value", parseFloat(e.target.value) || 0)}
                    className={`w-full ${formData.commission_type === "fixed" ? "pl-5" : "pl-2"} pr-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div>
            <SectionLabel>Summary</SectionLabel>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-center">
                <p className="text-[9px] font-bold uppercase tracking-wide text-amber-600 mb-0.5">Client Total</p>
                <p className="text-sm font-extrabold text-amber-700">₹{lineTotal.toLocaleString("en-IN")}</p>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-2.5 text-center">
                <p className="text-[9px] font-bold uppercase tracking-wide text-rose-500 mb-0.5">Vendor Total</p>
                <p className="text-sm font-extrabold text-rose-600">₹{totalVendorCost.toLocaleString("en-IN")}</p>
              </div>
              <div className={`rounded-xl p-2.5 text-center border ${margin >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                <p className={`text-[9px] font-bold uppercase tracking-wide mb-0.5 ${margin >= 0 ? "text-emerald-600" : "text-rose-500"}`}>Margin</p>
                <p className={`text-sm font-extrabold ${margin >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                  ₹{margin.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <SectionLabel>Notes</SectionLabel>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Special requirements or instructions..."
              rows={2}
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-stone-100 bg-white px-4 py-3 flex gap-2 flex-shrink-0">
          <button
            onClick={() => { resetForm(); onClose(); }}
            className="px-5 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm shadow-blue-200"
          >
            {submitting ? "Adding..." : "Add Vendor to Event"}
          </button>
        </div>
      </div>
    </div>
  );
}
